import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const NotificationsContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastNotificationTime, setLastNotificationTime] = useState({});
  const [isUserActive, setIsUserActive] = useState(true);
  const [intervals, setIntervals] = useState({});

  // Fetch user data and role
  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Decode JWT token to see what role is in the token
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        console.log('Notifications: Role in JWT token:', tokenPayload.role);
      } catch (e) {
        console.log('Notifications: Could not decode JWT token');
      }

      const response = await fetch(`${API_ENDPOINTS.AUTH}/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
        console.log('Notifications: User role fetched from API:', data.role);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Initialize notifications from localStorage (only when logged in, so we don't load another user's cache)
  const initializeNotificationsFromStorage = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const storedNotifications = JSON.parse(localStorage.getItem('notifications') || '[]');
      const storedLastNotificationTime = JSON.parse(localStorage.getItem('lastNotificationTime') || '{}');
      
      if (storedNotifications.length > 0) {
        setNotifications(storedNotifications);
        setLastNotificationTime(storedLastNotificationTime);
        console.log('Notifications: Initialized', storedNotifications.length, 'notifications from localStorage');
      }
    } catch (error) {
      console.error('Error initializing notifications from localStorage:', error);
    }
  };

  // Clear notification state (e.g. on logout so next user doesn't see previous user's notifications)
  const clearNotificationState = () => {
    setNotifications([]);
    setLastNotificationTime({});
    localStorage.removeItem('notifications');
    localStorage.removeItem('lastNotificationTime');
  };

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    // Fetch for all user roles
    if (!userRole) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      // Map user roles to their respective endpoints.
      // Only program-level roles get notifications here. Superuser and institutional_admin
      // have no endpoint so they never receive director or other role-specific notifications.
      const notificationEndpoints = {
        'director': `${API_ENDPOINTS.DIRECTOR}/notifications`,
        'resident': `${API_ENDPOINTS.RESIDENT}/notifications`,
        'attending': `${API_ENDPOINTS.ATTENDING}/notifications`,
        'administrative assistant': `${API_ENDPOINTS.ADMINISTRATIVE}/notifications`,
        'medical student': `${API_ENDPOINTS.MEDICAL_STUDENT}/notifications`
        // superuser, institutional_admin: intentionally no endpoint — they do not see director notifications
      };

      const endpoint = notificationEndpoints[userRole];
      if (!endpoint) {
        console.log('Notifications: No endpoint found for role:', userRole);
        return;
      }

      console.log('Notifications: Making request to:', endpoint);
      console.log('Notifications: User role:', userRole);
      console.log('Notifications: Token available:', !!token);

      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Notifications: Response status:', response.status);
      console.log('Notifications: Response ok:', response.ok);

      if (response && response.ok) {
        const data = await response.json();
        console.log('Notifications: Backend response data:', data);
        
        // Use setState callback to ensure we have the current state
        setNotifications(prevNotifications => {
          const evaluationNotifications = prevNotifications.filter(n => n.type === 'evaluation');
          const allNotifications = [...data, ...evaluationNotifications];
          console.log('Notifications: Fetched', data.length, 'backend notifications and preserved', evaluationNotifications.length, 'evaluation notifications');
          return allNotifications;
        });
      } else if (response) {
        const errorText = await response.text();
        console.error('Notifications: Fetch failed', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch evaluations and check for notifications
  const fetchEvaluations = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Only director and attending roles get evaluation notifications
      if (userRole !== 'director' && userRole !== 'attending') {
        console.log('Notifications: User role not eligible for evaluation notifications:', userRole);
        return;
      }
      
      console.log('Notifications: Fetching evaluations for role:', userRole);
      console.log('Notifications: Token available:', !!token);
      
      // Both director and attending can use the same endpoint
      const endpoint = `${API_ENDPOINTS.DIRECTOR}/evaluations-by-type/all`;
      console.log('Notifications: Making request to:', endpoint);
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // The response is an array of templates with evaluations
      const templates = await response.json();
      console.log('Notifications: Received templates:', templates.length);
      
      const allEvaluations = templates.flatMap(template => 
        template.evaluations.map(evaluation => ({
          ...evaluation,
          title: evaluation.title || template.title,
          evaluationType: template.evaluationType
        }))
      );
      
      console.log('Notifications: Total evaluations found:', allEvaluations.length);
      checkForAvailableEvaluations(allEvaluations);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status
      });
    }
  };

  // Check for available evaluations and create notifications
  const checkForAvailableEvaluations = (evaluations) => {
    const now = new Date();
    console.log('Notifications: Checking for available evaluations at:', now);
    
    // Only pending evaluations
    const availableEvaluations = evaluations.filter(evaluation => 
      evaluation.startTime && 
      new Date(evaluation.startTime) <= now && 
      evaluation.status === 'pending'
    );

    // Remove notifications for completed evaluations
    setNotifications(prev => 
      prev.filter(notif => {
        if (notif.type !== 'evaluation') return true;
        const evalId = notif.data?.id;
        const stillPending = availableEvaluations.some(e => e.id === evalId);
        return stillPending;
      })
    );

    // Add notifications for new available evaluations
    availableEvaluations.forEach(evaluation => {
      const notificationId = `eval-${evaluation.id}`;
      const existingNotification = notifications.find(notif => notif.id === notificationId);
      if (!existingNotification) {
        addNotification(evaluation, 'evaluation');
      }
    });
  };

  // Add a new notification
  const addNotification = (data, type = 'evaluation') => {
    if (type === 'evaluation' && data.status !== 'pending') {
      // Don't persist notification for completed evaluations
      return;
    }
    let notificationId, title, body;
    
    switch (type) {
      case 'evaluation':
        notificationId = `eval-${data.id}`;
        title = "Evaluation Available";
        body = `${data.title} for ${data.resident?.firstName} ${data.resident?.lastName} is available for completion.`;
        break;
      case 'worklog':
        notificationId = `worklog-${data.id}`;
        title = "Work Log Completed";
        body = `${data.resident?.firstName} ${data.resident?.lastName} has completed their work log for ${data.month}/${data.year}.`;
        break;
      case 'leaveRequest':
        notificationId = `leave-${data.id}`;
        title = "Leave Request Submitted";
        body = `${data.resident?.firstName} ${data.resident?.lastName} has submitted a ${data.type} request for ${data.startDate} to ${data.endDate}.`;
        break;
      case 'eventReminder':
        notificationId = `event-reminder-${data.eventId}`;
        title = data.title || "Event Reminder";
        body = data.body || `Your event "${data.eventTitle}" starts in 15 minutes.`;
        break;
      default:
        return;
    }
    
    // Check if this notification already exists and add it if it doesn't
    setNotifications(prevNotifications => {
      const existingNotification = prevNotifications.find(notif => notif.id === notificationId);
      if (existingNotification) {
        return prevNotifications; // Don't add duplicate
      }
      
      const newNotification = {
        id: notificationId,
        title,
        body,
        type,
        data,
        createdAt: new Date()
      };
      
      const updatedNotifications = [...prevNotifications, newNotification];
      
      // Save to localStorage
      localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
      
      console.log('Notifications: Added notification:', { type, title, body });
      
      return updatedNotifications;
    });
    
    // Update last notification time
    const newLastNotificationTime = {
      ...lastNotificationTime,
      [notificationId]: new Date()
    };
    setLastNotificationTime(newLastNotificationTime);
    localStorage.setItem('lastNotificationTime', JSON.stringify(newLastNotificationTime));
  };

  // Add work log completion notification
  const addWorkLogNotification = (resident, month, year) => {
    addNotification({
      id: `${resident.id}-${month}-${year}`,
      resident,
      month,
      year
    }, 'worklog');
  };

  // Add leave request notification
  const addLeaveRequestNotification = (request) => {
    addNotification(request, 'leaveRequest');
  };

  // Dismiss a single notification
  const dismissNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Check if the notification has a numeric ID (from the backend)
      if (!isNaN(notificationId)) {
        // Use role-specific endpoints
        const notificationEndpoints = {
          'director': `${API_ENDPOINTS.DIRECTOR}/notifications`,
          'resident': `${API_ENDPOINTS.RESIDENT}/notifications`,
          'attending': `${API_ENDPOINTS.ATTENDING}/notifications`,
          'administrative assistant': `${API_ENDPOINTS.ADMINISTRATIVE}/notifications`,
          'medical student': `${API_ENDPOINTS.MEDICAL_STUDENT}/notifications`
        };

        const baseEndpoint = notificationEndpoints[userRole];
        if (!baseEndpoint) {
          console.error('Notifications: No endpoint found for role:', userRole);
          return;
        }

        const response = await fetch(`${baseEndpoint}/${notificationId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete notification from the backend');
        }
        
        console.log('Notifications: Successfully deleted notification from backend:', notificationId);
      }

      // Remove from the frontend state
      setNotifications(prev => {
        const updatedNotifications = prev.filter(n => n.id !== notificationId);
        
        // Update localStorage with the new state
        localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
        
        // Also clean up any evaluation-specific notifications from localStorage
        if (typeof notificationId === 'string' && notificationId.startsWith('eval-')) {
          const lastNotificationTime = JSON.parse(localStorage.getItem('lastNotificationTime') || '{}');
          delete lastNotificationTime[notificationId];
          localStorage.setItem('lastNotificationTime', JSON.stringify(lastNotificationTime));
        }
        
        return updatedNotifications;
      });

      console.log('Notifications: Successfully removed notification from frontend state:', notificationId);

    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Create a list of all backend notification IDs
      const backendNotificationIds = notifications
        .filter(n => !isNaN(n.id))
        .map(n => n.id);

      // Send a request to the backend to delete them all
      if (backendNotificationIds.length > 0) {
        console.log('Notifications: Deleting', backendNotificationIds.length, 'notifications from backend');
        
        // Use role-specific endpoints
        const notificationEndpoints = {
          'director': `${API_ENDPOINTS.DIRECTOR}/notifications`,
          'resident': `${API_ENDPOINTS.RESIDENT}/notifications`,
          'attending': `${API_ENDPOINTS.ATTENDING}/notifications`,
          'administrative assistant': `${API_ENDPOINTS.ADMINISTRATIVE}/notifications`,
          'medical student': `${API_ENDPOINTS.MEDICAL_STUDENT}/notifications`
        };

        const baseEndpoint = notificationEndpoints[userRole];
        if (!baseEndpoint) {
          console.error('Notifications: No endpoint found for role:', userRole);
          return;
        }
        
        // Delete notifications one by one from the backend
        await Promise.all(
          backendNotificationIds.map(id => 
            fetch(`${baseEndpoint}/${id}`, {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
          )
        );
        
        console.log('Notifications: Successfully deleted all notifications from backend');
      }
      
      // Clear all notifications from the frontend state
      setNotifications([]);
      
      // Clear localStorage
      localStorage.removeItem('notifications');
      localStorage.removeItem('lastNotificationTime');
      
      console.log('Notifications: Successfully cleared all notifications from frontend and localStorage');
      
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  // Get notification count
  const getNotificationCount = () => {
    // Remove the isRead filter since backend notifications don't have this property
    return notifications.length;
  };

  // Handle user activity detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsUserActive(isVisible);
      console.log('Notifications: User activity changed - visible:', isVisible);
    };

    const handleUserActivity = () => {
      if (!isUserActive) {
        setIsUserActive(true);
        console.log('Notifications: User became active');
      }
    };

    // Set up event listeners for user activity
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('scroll', handleUserActivity);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('scroll', handleUserActivity);
    };
  }, [isUserActive]);

  // Fetch evaluations on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // No user logged in — clear any stale notifications from a previous user on this device
      clearNotificationState();
    } else {
      initializeNotificationsFromStorage();
    }
    fetchUserData();
  }, []);

  // When user logs out (same device), clear notification state so next user doesn't see previous user's notifications
  useEffect(() => {
    const onLogout = () => clearNotificationState();
    window.addEventListener('app-logout', onLogout);
    return () => window.removeEventListener('app-logout', onLogout);
  }, []);

  // Fetch data when userRole is available and manage intervals based on user activity
  useEffect(() => {
    if (userRole) {
      // Fetch initial data
      fetchNotifications();
      fetchEvaluations();
      
      // Clear any existing intervals
      Object.values(intervals).forEach(interval => clearInterval(interval));
      
      // Set up periodic checks only when user is active
      const evaluationInterval = setInterval(() => {
        if (isUserActive) {
          fetchEvaluations();
        }
      }, 60 * 60 * 1000); // Every hour
      
      const notificationInterval = setInterval(() => {
        if (isUserActive) {
          fetchNotifications();
        }
      }, 30 * 1000); // Every 30 seconds
      
      // Store intervals for cleanup
      setIntervals({ evaluationInterval, notificationInterval });
      
      return () => {
        clearInterval(evaluationInterval);
        clearInterval(notificationInterval);
      };
    }
  }, [userRole, isUserActive]);

  const value = {
    notifications,
    dismissNotification,
    clearAllNotifications,
    getNotificationCount,
    fetchEvaluations,
    fetchNotifications,
    loading,
    isUserActive
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}; 